import React, { useCallback } from "react";
import { Pressable } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import {
  useProductAddFavorites,
  useProductDeleteFavorites,
} from "@apis/collection";
import { Diamond } from "@src/types/DiamondDetail";
import { ProductCategory } from "@utils/constants";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { VDBText } from "@components/atoms";
interface UseFavoritesManagerParams {
  productType: ProductCategory;
  t: any; // i18n translation function
  theme: any; // theme object
  scrollValue: any; // for toast positioning
  bottom: number; // for toast positioning
  products: Diamond[]; // current product list
  getToastBottomMargin: (scrollValue: any, bottom: number) => number;
  onShowCollectionModal?: (id: number, item: Diamond) => void;
  onSetRemoveFavoritesItem?: (value: boolean) => void;
}

export function useFavoritesManager({
  productType,
  t,
  theme,
  scrollValue,
  bottom,
  products,
  getToastBottomMargin,
  onShowCollectionModal,
  onSetRemoveFavoritesItem,
}: UseFavoritesManagerParams) {
  const queryClient = useQueryClient();
  const addToFavorites = useProductAddFavorites(productType);
  const removeFromFavorites = useProductDeleteFavorites();

  const updateFavorite = useCallback(
    (isFavorite: boolean, items: Diamond[]) => {
      const ids = new Set(items.map((e) => e.id));
      queryClient.setQueriesData(
        { queryKey: ["SEARCH_RESULTS"] },
        (data: any) => {
          if (!data?.pages) return data;

          let changed = false;
          const newPages = data.pages.map((page: any) => {
            const body = page?.data?.response?.body;
            if (!body) return page;
            const key = body.diamonds
              ? "diamonds"
              : body.gemstones
              ? "gemstones"
              : body.jewelries
              ? "jewelries"
              : null;
            if (!key || !body[key]) return page;

            const mItems = body[key];
            if (!mItems.some((it: Diamond) => ids.has(it.id))) return page;

            changed = true;
            return {
              ...page,
              data: {
                ...page.data,
                response: {
                  ...page.data.response,
                  body: {
                    ...body,
                    [key]: mItems.map((it: Diamond) =>
                      ids.has(it.id) ? { ...it, is_favorite: isFavorite } : it
                    ),
                  },
                },
              },
            };
          });

          return changed ? { ...data, pages: newPages } : data;
        }
      );
    },
    [queryClient]
  );

  const showToastMessage = useCallback(
    (item: Diamond, isFavorite: boolean) => {
      const title = isFavorite
        ? `${item.short_title} ${t("searchResult.addToFavorites.text")}`
        : `${item.short_title} ${t("searchResult.removeFromFavorites.text")}`;

      const body = isFavorite
        ? React.createElement(
            Pressable,
            {
              onPress: () => {
                onSetRemoveFavoritesItem?.(true);
                onShowCollectionModal?.(item?.id ?? 0, item);
              },
            },
            React.createElement(
              VDBText,
              { color: theme["link-static"] },
              t("searchResult.tapToChangeCollection.text")
            )
          )
        : "";

      showToast(
        title,
        body,
        ToastTypes.SUCCESS,
        true,
        true,
        getToastBottomMargin(scrollValue, bottom)
      );
    },
    [
      t,
      theme,
      scrollValue,
      bottom,
      getToastBottomMargin,
      onShowCollectionModal,
      onSetRemoveFavoritesItem,
    ]
  );

  const favoriteFunctionClicked = useCallback(
    (isFavorite: boolean, item: Diamond) => {
      const _payload = {
        type: "mix",
        diamond_id: item?.id ?? 0,
        ...([
          ProductCategory.LAB_GROWN_DIAMOND,
          ProductCategory.DIAMOND,
        ].includes(productType) && {
          lab_grown: productType === ProductCategory.LAB_GROWN_DIAMOND,
        }),
        ...(productType === ProductCategory.GEMSTONE && {
          gemstone_id: item?.id ?? 0,
        }),
        ...(productType === ProductCategory.JEWELRY && {
          stock_item_id: item?.id ?? 0,
        }),
      };

      if (
        !isFavorite &&
        !(addToFavorites.isPending || removeFromFavorites.isPending)
      ) {
        addToFavorites.mutate(
          {
            favorite: _payload,
          },
          {
            onSuccess: () => {
              updateFavorite(!isFavorite, [item]);
              showToastMessage(item, !isFavorite);
            },
          }
        );
      } else {
        if (!(addToFavorites.isPending || removeFromFavorites.isPending)) {
          removeFromFavorites.mutate(
            {
              id: item?.id ?? 0,
            },
            {
              onSuccess: () => {
                updateFavorite(!isFavorite, [item]);
                showToastMessage(item, !isFavorite);
              },
            }
          );
        }
      }
    },
    [
      productType,
      addToFavorites,
      removeFromFavorites,
      updateFavorite,
      showToastMessage,
    ]
  );

  const favoriteRemoved = useCallback(
    (itemId: Array<number>) => {
      if (itemId && itemId.length > 0) {
        const productItem = products.find(
          (product: any) => product?.id === itemId?.[0]
        );
        if (productItem?.is_favorite) {
          updateFavorite(false, [productItem]);
        }
      }
    },
    [products, updateFavorite]
  );

  return {
    favoriteFunctionClicked,
    favoriteRemoved,
    updateFavorite,
    isAddingToFavorites: addToFavorites.isPending,
    isRemovingFromFavorites: removeFromFavorites.isPending,
    isLoading: addToFavorites.isPending || removeFromFavorites.isPending,
  };
}
